/**
 * LarkConnectModal — credential entry form for Lark self-built apps.
 *
 * Shared between IntegrationsPage and MarketplaceDetailModal (see
 * GitlabConnectModal docs for the rationale).
 *
 * Lark connection takes four fields:
 *   - App ID + App Secret  (required, from Developer Console → Credentials)
 *   - Verification Token   (optional, only if you want inbound events)
 *   - Encrypt Key          (optional, only if Lark-side event encryption is on)
 */

import React, { useEffect, useState } from 'react';
import { IonModal, IonButton, IonSpinner } from '@ionic/react';
import { useConnectLark } from '../../hooks/useIntegrations';
import '../../pages/IntegrationsPage/IntegrationsPage.css';

export default function LarkConnectModal({ isOpen, onClose, onConnected }) {
  const [appId, setAppId] = useState('');
  const [appSecret, setAppSecret] = useState('');
  const [verificationToken, setVerificationToken] = useState('');
  const [encryptKey, setEncryptKey] = useState('');
  const [error, setError] = useState('');
  const [connecting, setConnecting] = useState(false);
  const mutation = useConnectLark();

  useEffect(() => {
    if (isOpen) {
      setAppId('');
      setAppSecret('');
      setVerificationToken('');
      setEncryptKey('');
      setError('');
      setConnecting(false);
    }
  }, [isOpen]);

  const handleSubmit = () => {
    if (!appId.trim() || !appSecret.trim()) {
      setError('App ID and App Secret are required.');
      return;
    }
    setConnecting(true);
    setError('');
    mutation.mutate(
      {
        appId: appId.trim(),
        appSecret: appSecret.trim(),
        verificationToken: verificationToken.trim(),
        encryptKey: encryptKey.trim(),
      },
      {
        onSuccess: () => {
          setConnecting(false);
          onConnected?.({ webhookReady: !!verificationToken.trim() });
          onClose?.();
        },
        onError: (err) => {
          setConnecting(false);
          setError(err?.message || 'Failed to connect Lark. Please check your App ID and App Secret.');
        },
      },
    );
  };

  return (
    <IonModal
      isOpen={isOpen}
      onDidDismiss={() => onClose?.()}
      className="lark-connect-modal"
    >
      {/* See GitlabConnectModal for why this stopPropagation matters:
          React events traverse portals, so without it a click in this
          modal bubbles up to the marketplace modal's backdrop and
          closes everything. */}
      <div
        className="lark-modal-content"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <h2>Connect Lark</h2>
        <p className="lark-modal-description">
          Enter the <code>App ID</code> and <code>App Secret</code> from your Lark self-built app (Lark Developer Console → <code>Credentials &amp; Basic Info</code>). Add the <code>Verification Token</code> and optional <code>Encrypt Key</code> below if you want Lark events to trigger Zibby workflows.
        </p>

        <div className="lark-form-group">
          <label className="lark-form-label">App ID</label>
          <input
            type="text"
            value={appId}
            placeholder="cli_xxxxxxxxxxxxxxxx"
            onChange={(e) => { setAppId(e.target.value || ''); setError(''); }}
            className="lark-form-input"
            disabled={connecting}
          />
        </div>

        <div className="lark-form-group">
          <label className="lark-form-label">App Secret</label>
          <input
            type="password"
            value={appSecret}
            placeholder="••••••••••••••••••••••••••••••••"
            onChange={(e) => { setAppSecret(e.target.value || ''); setError(''); }}
            className="lark-form-input"
            disabled={connecting}
          />
        </div>

        <div className="lark-form-group">
          <label className="lark-form-label">
            Verification Token{' '}
            <span style={{ opacity: 0.6, fontWeight: 400 }}>(optional — required for inbound events)</span>
          </label>
          <input
            type="password"
            value={verificationToken}
            placeholder="from Event Subscriptions → Verification Token"
            onChange={(e) => { setVerificationToken(e.target.value || ''); setError(''); }}
            className="lark-form-input"
            disabled={connecting}
          />
        </div>

        <div className="lark-form-group">
          <label className="lark-form-label">
            Encrypt Key{' '}
            <span style={{ opacity: 0.6, fontWeight: 400 }}>(optional — only if enabled in Lark)</span>
          </label>
          <input
            type="password"
            value={encryptKey}
            placeholder="from Event Subscriptions → Encrypt Key"
            onChange={(e) => { setEncryptKey(e.target.value || ''); setError(''); }}
            className="lark-form-input"
            disabled={connecting}
          />
        </div>

        {error && <p className="lark-modal-error">{error}</p>}

        <div className="lark-modal-buttons">
          <IonButton
            expand="block"
            onClick={handleSubmit}
            disabled={connecting || !appId.trim() || !appSecret.trim()}
          >
            {connecting ? <IonSpinner name="crescent" /> : 'Connect'}
          </IonButton>
          <IonButton
            expand="block"
            fill="clear"
            onClick={() => onClose?.()}
            disabled={connecting}
          >
            Cancel
          </IonButton>
        </div>
      </div>
    </IonModal>
  );
}
